/**
 * Uploads from this browser tab (file, folder, drag and drop, chat composer), as the Transfers
 * list shows them next to server-side imports. `uploadFile` reports into it; components read it
 * with `useUploads`.
 *
 * Kept in memory on purpose: an upload lives as long as the tab (a reload cancels it), so its
 * entry does too. Resumable uploads (plan Phase 4) will feed this same store.
 */

export type UploadStatus = 'uploading' | 'done' | 'failed'

export interface UploadEntry {
  id: string
  name: string
  /** Destination folder relative to the user's root, '' for the root. */
  destinationPath: string
  size: number
  loaded: number
  status: UploadStatus
  error?: string
  startedAt: number
  finishedAt?: number
}

/** Finished entries beyond this many are dropped, oldest first. */
export const MAX_KEPT_UPLOADS = 50

type Listener = () => void

let entries: readonly UploadEntry[] = []
let nextId = 0
const listeners = new Set<Listener>()

function setEntries(next: readonly UploadEntry[]): void {
  entries = next
  listeners.forEach((listener) => listener())
}

function update(id: string, change: Partial<UploadEntry>): void {
  if (!entries.some((e) => e.id === id)) return
  setEntries(entries.map((e) => (e.id === id ? {...e, ...change} : e)))
}

/** Newest first. Stable between changes, so it can back useSyncExternalStore. */
export function getUploads(): readonly UploadEntry[] {
  return entries
}

export function subscribeUploads(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isUploadActive(entry: UploadEntry): boolean {
  return entry.status === 'uploading'
}

/** Adds an upload that is starting; returns its id. */
export function trackUpload(input: {name: string; destinationPath: string; size: number}): string {
  nextId += 1
  const id = `upload-${nextId}`
  const entry: UploadEntry = {...input, id, loaded: 0, status: 'uploading', startedAt: Date.now()}
  const finished = entries.filter((e) => !isUploadActive(e))
  const overflow = new Set(finished.slice(Math.max(0, MAX_KEPT_UPLOADS - 1)).map((e) => e.id))
  setEntries([entry, ...entries.filter((e) => !overflow.has(e.id))])
  return id
}

export function reportUploadProgress(id: string, loaded: number): void {
  update(id, {loaded})
}

export function finishUpload(id: string): void {
  const entry = entries.find((e) => e.id === id)
  update(id, {status: 'done', loaded: entry?.size ?? 0, finishedAt: Date.now()})
}

export function failUpload(id: string, error: string): void {
  update(id, {status: 'failed', error, finishedAt: Date.now()})
}

/** Removes finished uploads from the list; uploads still running stay. */
export function dismissUploads(ids: string[]): void {
  const remove = new Set(ids)
  setEntries(entries.filter((e) => isUploadActive(e) || !remove.has(e.id)))
}

export function clearFinishedUploads(): void {
  setEntries(entries.filter(isUploadActive))
}

/** Test hook. */
export function resetUploads(): void {
  setEntries([])
}

/** 'a/b' for the key 'a/b/file.txt', '' for 'file.txt'. */
export function uploadDestination(filePath: string): string {
  const parts = filePath.split('/')
  return parts.slice(0, -1).join('/')
}
